import { type TargetsTypes, type TransformationTypes } from 'src/biddingSignals/types'
import fetch, { baseClient } from './fetch'

const client = fetch()
const transferClient = baseClient({ headers: { 'content-type': 'text/csv' } })
export const paths = {
  enhancedSignals: '/enhanced_signals'
}
type EventName = 'precis_ecomm' | 'purchase' | ''
export interface EnhancedSignalsResp {
  bucketId: string
  clientId: number
  configName: string
  createdAt: number
  createdBy: number
  deleted: boolean
  enabled: boolean
  id: number
  targetCurrency: string | null
  targets: [
    {
      apiSecret: string
      enabled: true
      eventName: EventName
      measurementId: string
      targetId: TargetsTypes
      url: string
      propertyId: string
      sgtmToken: string
      gadsAccountId: string
      authAccount: string
      conversionActionId: string
      firebaseAppId: string
      currency: string
      accessToken: string
      pixelId: string
    }
  ]
  transformations: [
    {
      enabled: boolean
      lookupRules: Array<
        Pick<LookupRule, 'key' | 'operation' | 'lookupDataInputOption' | 'lookupFile'> & {
          sourceAccount?: SourceAccount
        }
      >
      transformId: TransformationTypes
      sourceAccounts: SourceAccounts
      lookupDataInputOption: 'file' | 'google_sheets'
      calculationMethod?: 'total_reduction' | 'item_reduction'
    }
  ]
  updatedAt: number
  updatedBy: number
  version: 'v1'
}

export interface ConfigIdEnhancedSignalsReq {
  clientId: number
  configId: number
}

export interface CreateSignedUrlReq {
  clientId: number
  configId: number
  fileName: string
  transferType: 'upload' | 'read'
}
export interface CreateUploadUrlResp {
  signedURL: string
}

export interface LookupRule {
  key: string
  operation: 'multiply' | 'divide' | 'add' | 'subtract' | ''
  lookupFile: {
    fileData?: File
    fileName: string
  }
  id?: string
  isNewLookupRule: boolean
  sourceAccountAccountId?: number
  sourceAccountWorksheetId?: number
  lookupDataInputOption?: 'file' | 'google_sheets'
}

export interface SourceAccounts {
  productCostList?: SourceAccount
  marginFallback?: SourceAccount
}

export interface SourceAccount {
  accountId?: number
  worksheetId?: number
  fileName?: string | null
  lookupType?: string | null
}

export type MappedLookupRule = Omit<
  LookupRule,
  'isNewLookupRule' | 'sourceAccountAccountId' | 'sourceAccountWorksheetId'
> & {
  sourceAccount?: SourceAccount
}

export interface TransformationsReq {
  transformId: TransformationTypes
  enabled: boolean
  files?: string[]
  lookupRules?: MappedLookupRule[]
  sourceAccounts?: SourceAccounts
  lookupDataInputOption?: 'file' | 'google_sheets'
  calculationMethod?: 'total_reduction' | 'item_reduction'
}

export interface TargetsReq {
  apiSecret?: string
  enabled: boolean
  eventName?: EventName
  measurementId?: string
  targetId: TargetsTypes
  url?: string
  propertyId?: string
  sgtmToken?: string
  bearerPath?: string
  firebaseAppId?: string
  gadsAccountId?: string
  authAccount?: string
  conversionActionId?: string
  currency?: string
  accessToken?: string
  pixelId?: string
}
export interface EnhancedSignalsReq {
  version: 'v1'
  configName: string
  id?: number
  clientId: number
  enabled: boolean
  targetCurrency: string | null
  transformations: TransformationsReq[]
  targets: TargetsReq[]
  bucketId?: string
  deleted?: boolean
  updatedBy?: number
  createdBy?: number
  createdAt?: number
}
export interface EditEnhancedSignalsReq extends EnhancedSignalsReq {
  id: number
}

export interface LookupFileMetadataResp {
  bucketName: string
  contentType: string
  fileName: string
  fileSize: number
  lastModified: string
  timeCreated: string
}

export const getAllEnhancedSignalsByClient = async (clientId: number): Promise<EnhancedSignalsResp[]> => {
  return await client.get<EnhancedSignalsResp[]>({
    path: `${paths.enhancedSignals}?client_id=${clientId}`
  })
}

export const getEnhancedSignalById = async (clientId: number, configId: number): Promise<EnhancedSignalsResp> => {
  return await client.get<EnhancedSignalsResp>({
    path: `${paths.enhancedSignals}/${configId}?client_id=${clientId}`
  })
}

export const createEnhancedSignals = async (data: EnhancedSignalsReq): Promise<EnhancedSignalsResp> => {
  return await client.post<EnhancedSignalsResp>({
    path: paths.enhancedSignals,
    data
  })
}

export const updateEnhancedSignals = async (data: EditEnhancedSignalsReq): Promise<EnhancedSignalsResp> => {
  return await client.put<EnhancedSignalsResp>({
    path: `${paths.enhancedSignals}/${data.id}`,
    data
  })
}

export const deleteEnhancedSignals = async (data: ConfigIdEnhancedSignalsReq): Promise<EnhancedSignalsResp> => {
  return await client.del<EnhancedSignalsResp>({
    path: `${paths.enhancedSignals}/${data.configId}?client_id=${data.clientId}`
  })
}

export const createSignedUrl = async (data: CreateSignedUrlReq): Promise<string> => {
  return await client.post<string>({
    path: `${paths.enhancedSignals}/${data.configId}/signed_url`,
    data: {
      clientId: data.clientId,
      transferType: data.transferType,
      fileName: data.fileName
    }
  })
}

export interface UploadFileReq {
  signedURL: string
  file: File
}

export const uploadFile = async ({ file, signedURL }: UploadFileReq): Promise<string> => {
  return await transferClient.put<string>({
    path: signedURL,
    data: file
  })
}

export interface DownloadFileReq {
  signedURL: string
}

export const downloadFile = async ({ signedURL }: DownloadFileReq): Promise<string> => {
  // return await transferClient.get<string>({
  //   path: signedURL
  // })
  // Proxy through Next.js API to avoid CORS issues with direct GCS access
  const proxyUrl = `/api/gcs/download?url=${encodeURIComponent(signedURL)}`
  return await transferClient.get<string>({
    path: proxyUrl
  })
}

export const getLookupFileMetadata = async (
  clientId: number,
  configId: number,
  fileName: string
): Promise<LookupFileMetadataResp> => {
  return await client.get<LookupFileMetadataResp>({
    path: `${paths.enhancedSignals}/${configId}/lookup_file_metadata?client_id=${clientId}&file_name=${fileName}`
  })
}

export const validateLookupData = async (
  clientId: number,
  accountId: number,
  lookupType: string,
  worksheetId: number
): Promise<any[]> => {
  return await client.get<any[]>({
    path: `${paths.enhancedSignals}/validate_google_sheets_input?client_id=${clientId}&account_id=${accountId}&lookup_type=${lookupType}&worksheet_id=${worksheetId}`
  })
}
